import { Component, Injectable } from '@angular/core';
import { VexLayoutService } from '@vex/services/vex-layout.service';
import { NavigationItem } from './navigation-item.interface';
import { BehaviorSubject, Observable, concatAll } from 'rxjs';
import { PermissionConstant } from '../../shared/constants/permission-constant';
import { RolePermissionService } from '../../shared/services/role-permission.service';
import { AuthService } from '../../shared/services/auth.service';
import { Helper } from '../../shared/constants/helper';
import { GlobalService } from '../../shared/services/global.service';
import { StorageService } from '../../shared/services/storage.service';

@Injectable({
  providedIn: 'root'
})

export class NavigationLoaderService {
  user: any;
  userData: any;
  private readonly _items: BehaviorSubject<NavigationItem[]> =
    new BehaviorSubject<NavigationItem[]>([]);

  get items$(): Observable<NavigationItem[]> {
    return this._items.asObservable();
  }

  constructor(private readonly layoutService: VexLayoutService,
              private rolesPermissionsService: RolePermissionService,
              private authService: AuthService
  ) {
    const self: any = StorageService.getItem('self');
    if (self && self['user_type'] === 'SUPERADMIN') {
      this.loadSuperAdminNavigation();
    } else if (self) {
      this.getSelfDetails();
    } else {
      this.loadNavigation();
    }
  }

  /**
   * Superadmin is platform-wide, not scoped to an organisation, so it gets
   * its own minimal nav instead of the shared tenant nav array below (whose
   * per-item `availableTo` filtering only actually runs for EMPLOYEE users).
   */
  loadSuperAdminNavigation(): void {
    this._items.next([
      {
        type: 'subheading',
        label: 'Platform',
        availableTo: ['SUPERADMIN'],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'Organisations',
            route: '/organisations',
            icon: 'mat:business',
            routerLinkActiveOptions: { exact: true },
            is_hidden: false,
            availableTo: ['SUPERADMIN']
          }
        ]
      }
    ]);
  }

  loadNavigation(): void {
    this._items.next([
      {
        type: 'subheading',
        label: 'Dashboard',
        availableTo: [
          'ORGANISATION',
          'ADMIN',
          'EMPLOYEE',
          PermissionConstant.ALL
        ],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'Home',
            route: '/home',
            icon: 'mat:home',
            routerLinkActiveOptions: { exact: true },
            is_hidden: false,
            availableTo: [
              'ORGANISATION',
              'ADMIN',
              'EMPLOYEE',
              PermissionConstant.ALL
            ]
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Company',
        availableTo: [
          'ORGANISATION',
          PermissionConstant.ADD_COMPANY,
          PermissionConstant.LIST_COMPANY
        ],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'Overview',
            route: '/general/company/overview',
            icon: 'mat:dashboard_customize',
            availableTo: [
              'ORGANISATION',
              PermissionConstant.ADD_COMPANY,
              PermissionConstant.LIST_COMPANY
            ],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Add',
            route: '/general/company/add',
            icon: 'mat:cast_for_education',
            availableTo: ['ORGANISATION', PermissionConstant.ADD_COMPANY],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'List',
            route: '/general/company/list',
            icon: 'mat:book',
            availableTo: ['ORGANISATION', PermissionConstant.LIST_COMPANY],
            is_hidden: false
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Employee',
        availableTo: [
          'ORGANISATION',
          'ADMIN',
          'EMPLOYEE',
          PermissionConstant.EMPLOYEE_OVERVIEW,
          PermissionConstant.ADD_EMPLOYEE,
          PermissionConstant.LIST_EMPLOYEE,
          PermissionConstant.EDIT_EMPLOYEE,
          PermissionConstant.ASSIGN_DEVICE,
          PermissionConstant.STATUS_EMPLOYEE,
          PermissionConstant.DELETE_EMPLOYEE,
          PermissionConstant.EMPLOYEE_RESIGNATION
        ],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'Overview',
            route: '/employees/overview',
            icon: 'mat:dashboard_customize',
            availableTo: [
              'ORGANISATION',
              'ADMIN',
              'EMPLOYEE',
              PermissionConstant.EMPLOYEE_OVERVIEW
            ],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Add',
            route: '/employees/add-employee',
            icon: 'mat:subject',
            availableTo: ['ADMIN', 'EMPLOYEE', PermissionConstant.ADD_EMPLOYEE],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'List',
            route: '/employees/employee-list',
            icon: 'mat:settings_overscan',
            is_hidden: false,
            availableTo: [
              'ORGANISATION',
              'ADMIN',
              'EMPLOYEE',
              PermissionConstant.LIST_EMPLOYEE,
              PermissionConstant.EDIT_EMPLOYEE,
              PermissionConstant.ASSIGN_DEVICE,
              PermissionConstant.STATUS_EMPLOYEE,
              PermissionConstant.DELETE_EMPLOYEE
            ]
          },
          {
            type: 'link',
            label: 'Resignation',
            route: '/employees/resignation',
            icon: 'mat:settings_overscan',
            is_hidden: false,
            availableTo: [
              'ORGANISATION',
              'ADMIN',
              'EMPLOYEE',
              PermissionConstant.EMPLOYEE_RESIGNATION
            ]
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Department',
        availableTo: [
          'ORGANISATION',
          'ADMIN',
          'EMPLOYEE',
          PermissionConstant.DEPARTMENT_OVERVIEW,
          PermissionConstant.ADD_DEPARTMENT,
          PermissionConstant.LIST_DEPARTMENT,
          PermissionConstant.UPDATE_DEPARTMENT,
          PermissionConstant.STATUS_DEPARTMENT,
          PermissionConstant.DELETE_DEPARTMENT
        ],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'Overview',
            route: '/general/department/overview',
            icon: 'mat:dashboard_customize',
            availableTo: [
              'ORGANISATION',
              'ADMIN',
              'EMPLOYEE',
              PermissionConstant.DEPARTMENT_OVERVIEW
            ],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Add',
            route: '/general/department/add',
            icon: 'mat:cast_for_education',
            availableTo: [
              'ADMIN',
              'EMPLOYEE',
              PermissionConstant.ADD_DEPARTMENT
            ],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'List',
            route: '/general/department/list',
            icon: 'mat:book',
            availableTo: [
              'ORGANISATION',
              'ADMIN',
              'EMPLOYEE',
              PermissionConstant.LIST_DEPARTMENT,
              PermissionConstant.UPDATE_DEPARTMENT,
              PermissionConstant.STATUS_DEPARTMENT,
              PermissionConstant.DELETE_DEPARTMENT
            ],
            is_hidden: false
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Designation',
        availableTo: [
          'ORGANISATION',
          'ADMIN',
          'EMPLOYEE',
          PermissionConstant.DESIGNATION_OVERVIEW,
          PermissionConstant.ADD_DESIGNATION,
          PermissionConstant.LIST_DESIGNATION,
          PermissionConstant.UPDATE_DESIGNATION,
          PermissionConstant.STATUS_DESIGNATION,
          PermissionConstant.DELETE_DESIGNATION
        ],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'Overview',
            route: '/general/designation/overview',
            icon: 'mat:dashboard_customize',
            availableTo: [
              'ORGANISATION',
              'ADMIN',
              'EMPLOYEE',
              PermissionConstant.DESIGNATION_OVERVIEW
            ],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Add',
            route: '/general/designation/add',
            icon: 'mat:cast_for_education',
            availableTo: [
              'ADMIN',
              'EMPLOYEE',
              PermissionConstant.ADD_DESIGNATION
            ],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'List',
            route: '/general/designation/list',
            icon: 'mat:book',
            availableTo: [
              'ORGANISATION',
              'ADMIN',
              'EMPLOYEE',
              PermissionConstant.LIST_DESIGNATION,
              PermissionConstant.UPDATE_DESIGNATION,
              PermissionConstant.STATUS_DESIGNATION,
              PermissionConstant.DELETE_DESIGNATION
            ],
            is_hidden: false
          }
        ]
      },
      {
        type: 'subheading',
        label: 'DD-Mapping',
        availableTo: [
          'ORGANISATION',
          'ADMIN',
          'EMPLOYEE',
          PermissionConstant.DDMAPPING_OVERVIEW,
          PermissionConstant.ADD_DDMAPPING,
          PermissionConstant.LIST_DDMAPPING,
          PermissionConstant.UPDATE_DDMAPPING,
          PermissionConstant.STATUS_DDMAPPING,
          PermissionConstant.DELETE_DDMAPPING
        ],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'Overview',
            route: '/general/dd-mapping/overview',
            icon: 'mat:dashboard_customize',
            availableTo: [
              'ORGANISATION',
              'ADMIN',
              'EMPLOYEE',
              PermissionConstant.DDMAPPING_OVERVIEW
            ],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Add',
            route: '/general/dd-mapping/add',
            icon: 'mat:cast_for_education',
            availableTo: [
              'ADMIN',
              'EMPLOYEE',
              PermissionConstant.ADD_DDMAPPING
            ],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'List',
            route: '/general/dd-mapping/list',
            icon: 'mat:book',
            availableTo: [
              'ORGANISATION',
              'ADMIN',
              'EMPLOYEE',
              PermissionConstant.LIST_DDMAPPING,
              PermissionConstant.UPDATE_DDMAPPING,
              PermissionConstant.STATUS_DDMAPPING,
              PermissionConstant.DELETE_DDMAPPING
            ],
            is_hidden: false
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Access',
        availableTo: [
          'ORGANISATION',
          'ADMIN',
          'EMPLOYEE',
          PermissionConstant.ACCESS_OVERVIEW,
          PermissionConstant.ACCESS_PERMISSIONS,
          PermissionConstant.ADD_PERMISSIONS,
          PermissionConstant.LIST_PERMISSIONS,
          PermissionConstant.SUB_PERMISSIONS,
          PermissionConstant.UPDATE_PERMISSIONS,
          PermissionConstant.STATUS_PERMISSIONS,
          PermissionConstant.DELETE_PERMISSIONS,
          PermissionConstant.ADD_SUBPERMISSIONS,
          PermissionConstant.UPDATE_SUBPERMISSIONS,
          PermissionConstant.DELETE_SUBPERMISSIONS,
          PermissionConstant.STATUS_SUBPERMISSIONS,
          PermissionConstant.ACCESS_ROLES,
          PermissionConstant.ADD_ROLE,
          PermissionConstant.LIST_ROLE,
          PermissionConstant.UPDATE_ROLE,
          PermissionConstant.DELETE_ROLE,
          PermissionConstant.ROLE_PERMISSION,
          PermissionConstant.STATUS_ROLE
        ],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'Overview',
            route: '/role-access/overview',
            icon: 'mat:dashboard_customize',
            availableTo: [
              'ORGANISATION',
              'ADMIN',
              'EMPLOYEE',
              PermissionConstant.ACCESS_OVERVIEW
            ],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Permission',
            route: '/role-access/permission',
            icon: 'mat:cast_for_education',
            availableTo: [
              'ORGANISATION',
              'ADMIN',
              'EMPLOYEE',
              PermissionConstant.ACCESS_PERMISSIONS,
              PermissionConstant.ADD_PERMISSIONS,
              PermissionConstant.LIST_PERMISSIONS,
              PermissionConstant.SUB_PERMISSIONS,
              PermissionConstant.UPDATE_PERMISSIONS,
              PermissionConstant.STATUS_PERMISSIONS,
              PermissionConstant.DELETE_PERMISSIONS,
              PermissionConstant.ADD_SUBPERMISSIONS,
              PermissionConstant.UPDATE_SUBPERMISSIONS,
              PermissionConstant.DELETE_SUBPERMISSIONS,
              PermissionConstant.STATUS_SUBPERMISSIONS
            ],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Role',
            route: '/role-access/role',
            icon: 'mat:book',
            availableTo: [
              'ORGANISATION',
              'ADMIN',
              'EMPLOYEE',
              PermissionConstant.ACCESS_ROLES,
              PermissionConstant.ADD_ROLE,
              PermissionConstant.LIST_ROLE,
              PermissionConstant.UPDATE_ROLE,
              PermissionConstant.DELETE_ROLE,
              PermissionConstant.ROLE_PERMISSION,
              PermissionConstant.STATUS_ROLE
            ],
            is_hidden: false
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Holiday',
        availableTo: [
          'ORGANISATION',
          'ADMIN',
          'EMPLOYEE',
          PermissionConstant.HOLIDAY_OVERVIEW,
          PermissionConstant.ADD_HOLIDAY,
          PermissionConstant.LIST_HOLIDAY,
          PermissionConstant.EDIT_HOLIDAY,
          PermissionConstant.DELETE_HOLIDAY
        ],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'Overview',
            route: '/holiday/overview',
            icon: 'mat:dashboard_customize',
            availableTo: [
              'ORGANISATION',
              'ADMIN',
              'EMPLOYEE',
              PermissionConstant.HOLIDAY_OVERVIEW
            ],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Add Holiday',
            route: '/holiday/add-holiday',
            icon: 'mat:person_add',
            availableTo: ['ADMIN', 'EMPLOYEE', PermissionConstant.ADD_HOLIDAY],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'List Holiday',
            route: '/holiday/list-holiday',
            icon: 'mat:diversity_3',
            availableTo: [
              'ORGANISATION',
              'ADMIN',
              'EMPLOYEE',
              PermissionConstant.LIST_HOLIDAY,
              PermissionConstant.EDIT_HOLIDAY,
              PermissionConstant.DELETE_HOLIDAY
            ],
            is_hidden: false
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Leave',
        availableTo: [
          'ORGANISATION',
          'ADMIN',
          'EMPLOYEE',
          PermissionConstant.ADD_LEAVE,
          PermissionConstant.LIST_LEAVE,
          PermissionConstant.UPDATE_LEAVE,
          PermissionConstant.VIEW_LEAVE_DOC,
          PermissionConstant.PREVIEW_LEAVE_MESSAGE,
          PermissionConstant.LEAVE_STATUS,
          PermissionConstant.DELETE_LEAVE
        ],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'Leave',
            route: 'leave/leave',
            icon: 'mat:diversity_3',
            availableTo: [
              'ORGANISATION',
              'ADMIN',
              'EMPLOYEE',
              PermissionConstant.ADD_LEAVE,
              PermissionConstant.LIST_LEAVE,
              PermissionConstant.UPDATE_LEAVE,
              PermissionConstant.VIEW_LEAVE_DOC,
              PermissionConstant.PREVIEW_LEAVE_MESSAGE,
              PermissionConstant.LEAVE_STATUS,
              PermissionConstant.DELETE_LEAVE
            ],
            is_hidden: false
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Device',
        availableTo: [
          'ORGANISATION',
          'ADMIN',
          'EMPLOYEE',
          PermissionConstant.ADD_DEVICE,
          PermissionConstant.LIST_DEVICE,
          PermissionConstant.DEVICE_OVERVIEW,
          PermissionConstant.DEVICE_STATUS,
          PermissionConstant.DEVICE_DELETE,
          PermissionConstant.DEVICE_IMAGE_UPLOAD,
          PermissionConstant.VIEW_DEVICE_DETAILS
        ],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'Overview',
            route: '/device/overview',
            icon: 'mat:dashboard_customize',
            availableTo: [
              'ORGANISATION',
              'ADMIN',
              'EMPLOYEE',
              PermissionConstant.DEVICE_OVERVIEW
            ],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Add Device',
            route: '/device/add-device',
            icon: 'mat:person_add',
            availableTo: ['ADMIN', 'EMPLOYEE', PermissionConstant.ADD_DEVICE],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'List Device',
            route: '/device/list-device',
            icon: 'mat:diversity_3',
            availableTo: [
              'ORGANISATION',
              'ADMIN',
              'EMPLOYEE',
              PermissionConstant.LIST_DEVICE,
              PermissionConstant.EDIT_DEVICE,
              PermissionConstant.DEVICE_STATUS,
              PermissionConstant.DEVICE_DELETE,
              PermissionConstant.DEVICE_IMAGE_UPLOAD,
              PermissionConstant.VIEW_DEVICE_DETAILS
            ],
            is_hidden: false
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Attendance',
        availableTo: [
          'ORGANISATION',
          'ADMIN',
          'EMPLOYEE',
          PermissionConstant.ADD_ATTENDANCE,
          PermissionConstant.LIST_ATTENDANCE,
          PermissionConstant.TODAY_ATTENDANCE
        ],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'Today Attendance',
            route: '/attendance/today-attendance',
            icon: 'mat:diversity_3',
            availableTo: [
              'ORGANISATION',
              'ADMIN',
              'EMPLOYEE',
              PermissionConstant.TODAY_ATTENDANCE
            ],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Monthly Attendance',
            route: '/attendance/monthly-attendance',
            icon: 'mat:diversity_3',
            availableTo: [
              'ORGANISATION',
              'ADMIN',
              'EMPLOYEE',
              PermissionConstant.ADD_ATTENDANCE
            ],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Employee Attendance',
            route: '/attendance/view-attendance',
            icon: 'mat:diversity_3',
            availableTo: ['EMPLOYEE', PermissionConstant.LIST_ATTENDANCE],
            is_hidden: false
          },
          {
            type: 'link',
            label: 'Attendance Report',
            route: '/attendance/attendance-report',
            icon: 'mat:event_available',
            availableTo: ['ORGANISATION', 'ADMIN'],
            is_hidden: false
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Complaints',
        availableTo: [
          'ORGANISATION',
          'ADMIN',
          'EMPLOYEE',
          PermissionConstant.COMPLAINTS,
          PermissionConstant.COMPLAINTS_ADD,
          PermissionConstant.COMPLAINTS_UPDATE,
          PermissionConstant.COMPLAINTS_DELETE,
          PermissionConstant.COMPLAINTS_STATUS,
          PermissionConstant.COMPLAINTS_REVIEW_STATUS
        ],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'Complaints',
            route: 'complaints',
            icon: 'mat:support_agent',
            availableTo: [
              'ORGANISATION',
              'ADMIN',
              'EMPLOYEE',
              PermissionConstant.COMPLAINTS_ADD,
              PermissionConstant.COMPLAINTS_UPDATE,
              PermissionConstant.COMPLAINTS_DELETE,
              PermissionConstant.COMPLAINTS_STATUS,
              PermissionConstant.COMPLAINTS_REVIEW_STATUS
            ],
            is_hidden: false
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Social',
        availableTo: [
          'EMPLOYEE',
          PermissionConstant.ALL
        ],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'Chat',
            route: 'chat',
            icon: 'mat:diversity_3',
            availableTo: [
              'EMPLOYEE',
              PermissionConstant.ALL
            ],
            is_hidden: false
          }
        ]
      },
      {
        type: 'subheading',
        label: 'Support',
        availableTo: [
          'EMPLOYEE',
          PermissionConstant.ALL
        ],
        is_hidden: false,
        children: [
          {
            type: 'link',
            label: 'Tickets',
            route: 'employee-tickets/list',
            icon: 'mat:confirmation_number',
            availableTo: [
              'EMPLOYEE',
              PermissionConstant.ALL
            ],
            is_hidden: false
          }
        ]
      }
    ]);
  }

  getSelfDetails() {
    this.authService.self().subscribe((data) => {
      if (data && data.id) {
        this.user = data;
        if (this.user && this.user['user_type'] === 'EMPLOYEE') {
          this.checkUserDetails();
        } else {
          if (!sessionStorage.getItem('reloaded')) {
            sessionStorage.setItem('reloaded', 'true');
            location.reload();
          } else {
            sessionStorage.removeItem('reloaded');
            this.loadNavigation();
          }
        }
      }
    });
  }

  checkUserDetails() {
    this.rolesPermissionsService.get({ role_id: this.user?.role_id }).subscribe((data: any) => {
      this.userData = data['data'];
      let rolePermissions: any = [];
      if (this.userData && this.userData.length > 0) {
        this.userData.forEach((subPermission: any) => {
          if (subPermission && subPermission['sub_permission'] && subPermission['sub_permission']['meta']) {
            rolePermissions.push(subPermission['sub_permission']['meta']);
          }
        });
      }
      this.loadNavigation();
      this._items.forEach((menu) => {
        menu.forEach((item) => {
          item['is_hidden'] = !Helper.permissionCheck(item['availableTo'], rolePermissions);
          if (item['children'] && item['children'].length > 0) {
            item['children'].forEach((child: any) => {
              child['is_hidden'] = !Helper.permissionCheck(child['availableTo'], rolePermissions);

              if (child['children'] && child['children'].length > 0) {
                child['children'].forEach((grandChild: any) => {
                  grandChild['is_hidden'] = !Helper.permissionCheck(grandChild['availableTo'], rolePermissions);
                });
                child['is_hidden'] = child['children'].every((c: any) => c['is_hidden']);
              }
            });
            item['is_hidden'] = item['children'].every((c: any) => c['is_hidden']);
          }
        });
      });
    });
  }

}
